import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RadioGroup } from 'shared/components';

import { ModalContainer, ModalPrimaryHeader } from 'shared/modals';
import { SmallLoader } from 'shared/components/loading';
import useHasMobileApp from 'src/utils/use-has-mobile-app';
import { GqlImageBanner, GqlImageBannerPlatform, ImageBannerPlatformType } from 'types/graphql';
import { InlineBanner } from 'src/components/inline-banner';
import { validateURL, validateAlt, displayBannerPlatforms } from './image-banner-modal.helper';
import { useImageBannerModal } from './use-image-banner-modal';

import {
  Content,
  LeftColumn,
  RightColumn,
  Block,
  Title,
  Body,
  HiddenInput,
  ImportantText,
  FileSelect,
  InputContainer,
  Input,
  ValidateCheckIcon,
  PlaceHolderBanner,
  MobilePlaceHolderBanner,
  Separator,
  StyledButton,
  DesktopBannerImage,
  MobileBannerImage,
  RightColumnContent,
} from './image-banner-modal.styles';
import ImageBannerPlatform from './image-banner-platform';

type ImageBannerModalProps = {
  isOpen?: boolean;
  onClose: () => void;
  addBanner: (banner: GqlImageBanner) => void;
  bannerToEdit: GqlImageBanner | null;
};

export const TEST_ID_IMAGE_BANNER_MODAL = 'image-banner-modal';
export const TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT = 'image-banner-modal-alt-input';
export const TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT_VALID = 'image-banner-modal-alt-input-valid';

const imgixParams = {
  fit: 'crop',
  crop: 'edges',
};

export function ImageBannerModal({ onClose, isOpen, addBanner, bannerToEdit }: ImageBannerModalProps): JSX.Element {
  const {
    alt,
    link: defaultLink,
    openInNewTab: defaultOpenInNewTab,
    isEditingBanner,
    isUploadingImage,
    isUploadingMobileImage,
    image,
    mobileImage,
    platforms,
    handleChangeLinkBehavior,
    handleClose,
    handleFile,
    handleSave,
    handleSetAlt,
    handleSetLink,
    handleSetPlatform,
  } = useImageBannerModal({ onClose, addBanner, bannerToEdit });

  const hasMobileApp = useHasMobileApp();

  const flags = useFlags();
  const showBannerSegmentation = flags['growth.ecomm.banner-segmentation.rollout'] ?? false;

  const buttonText = isEditingBanner ? 'EDIT FILE' : 'SELECT FILE';

  const linkBehaviorOptions = [
    {
      label: 'New tab',
      value: true,
    },
    {
      label: 'Current tab',
      value: false,
    },
  ];

  const webPlatform = platforms?.find((p) => p?.type === ImageBannerPlatformType.web);
  const kioskPlatform = platforms?.find((p) => p?.type === ImageBannerPlatformType.kiosk);
  const iosPlatform = platforms?.find((p) => p?.type === ImageBannerPlatformType.iosMobileApp);

  const requiresDesktopImage =
    !showBannerSegmentation || (webPlatform?.enabled ?? true) || (kioskPlatform?.enabled ?? true);
  const requiresMobileImage =
    !showBannerSegmentation || (webPlatform?.enabled ?? true) || ((iosPlatform?.enabled ?? true) && hasMobileApp);

  return (
    <ModalContainer
      isOpen={isOpen}
      onRequestClose={handleClose}
      width='min(930px, calc(100vw - 32px))'
      fancy
      alignItems='flex-start'
      pt='90px'
      ariaHideApp={false}
    >
      <ModalPrimaryHeader copy={isEditingBanner ? 'Edit an Image Banner' : 'Add an Image Banner'} close={handleClose} />
      <Content data-testid={TEST_ID_IMAGE_BANNER_MODAL}>
        <LeftColumn>
          {!showBannerSegmentation ? (
            <>
              <Block>
                <Title>Link</Title>
                <Body>
                  Add a link to your image. When your image is clicked, a customer will be directed to this link.
                </Body>
                <InputContainer>
                  <Input type='url' value={defaultLink} onChange={handleSetLink} />
                  <ValidateCheckIcon valid={validateURL(defaultLink) ? 1 : 0} />
                </InputContainer>
              </Block>

              <Block>
                <Title>Link Behavior</Title>
                <Body>Decide where your image banner link opens when a customer clicks it.</Body>
                <RadioGroup
                  options={linkBehaviorOptions}
                  display={['inline-block']}
                  defaultValue={defaultOpenInNewTab}
                  onChange={handleChangeLinkBehavior}
                />
              </Block>
            </>
          ) : (
            <>
              <Block>
                <Title>Platforms</Title>
                <Body>
                  Select the platforms where the image banner should be displayed, and include a link if applicable.
                  When the banner image is clicked, the customer wil be directed to the link provided per platform.
                </Body>
              </Block>

              {!requiresDesktopImage && !requiresMobileImage && (
                <Block>
                  <InlineBanner description='At least one platform required to create image banner.' severity='error' />
                </Block>
              )}

              {Object.values(displayBannerPlatforms).map((item) => {
                const isWebPlatform = item.value === ImageBannerPlatformType.web;
                const platform: GqlImageBannerPlatform = platforms?.find((p) => p?.type === item.value) ?? {
                  enabled: true,
                  link: isWebPlatform ? defaultLink : null,
                  openInNewTab: isWebPlatform ? defaultOpenInNewTab : true,
                  type: item.value,
                };

                if (platform.type === ImageBannerPlatformType.iosMobileApp && !hasMobileApp) {
                  return null;
                }

                return (
                  <ImageBannerPlatform
                    key={item.value}
                    description={item.description}
                    label={item.label}
                    linkBehaviorDescription={item.linkBehaviorDescription}
                    platform={platform}
                    setPlatform={handleSetPlatform}
                  />
                );
              })}
            </>
          )}
        </LeftColumn>
        <RightColumn>
          <RightColumnContent>
            {requiresDesktopImage && (
              <>
                {image ? (
                  <DesktopBannerImage {...imgixParams} src={`${image}?ar=6:2`} htmlAttributes={{ alt }} />
                ) : (
                  <PlaceHolderBanner>Desktop image preview will show here.</PlaceHolderBanner>
                )}

                <Block>
                  <Title>Desktop Image</Title>
                  <Body>
                    Uploaded images should be in JPEG, PNG, or GIF formats. Recommended dimensions are approximately
                    2400px wide. Height is flexible based on the image uploaded.
                  </Body>
                  <HiddenInput
                    id='image-banner-file-input'
                    type='file'
                    name='image-banner'
                    accept='image/png, image/jpeg, image/gif, image/webp, image/avif'
                    onChange={(e) => {
                      handleFile(e);
                      e.target.value = '';
                    }}
                    disabled={isUploadingImage || isUploadingMobileImage}
                  />
                  <FileSelect htmlFor='image-banner-file-input'>
                    {isUploadingImage ? <SmallLoader height={20} color='#0b99e6' /> : buttonText}
                  </FileSelect>
                </Block>
              </>
            )}

            {requiresMobileImage && (
              <>
                {mobileImage ? (
                  <MobileBannerImage {...imgixParams} src={`${mobileImage}?ar=2.22:1`} htmlAttributes={{ alt }} />
                ) : (
                  <MobilePlaceHolderBanner>Mobile image preview will show here.</MobilePlaceHolderBanner>
                )}

                <Block>
                  <Title>Mobile image</Title>
                  <Body>
                    Uploaded images should be in JPEG, PNG, or GIF formats. Recommended dimensions are approximately
                    800px wide. Height is flexible based on the image uploaded.
                    {(iosPlatform?.enabled ?? true) && hasMobileApp && (
                      <div>
                        <br />
                        <ImportantText>Important: </ImportantText>
                        If using this image for your mobile app, to comply with App Store guidelines, do not upload
                        images that include vape products such as disposables, pods, cartridges, and vaporizers
                        themselves. This may result in your app being rejected.
                      </div>
                    )}
                  </Body>
                  <HiddenInput
                    id='mobile-image-banner-file-input'
                    type='file'
                    name='mobile-image-banner'
                    accept='image/png, image/jpeg, image/gif, image/webp, image/avif'
                    onChange={(e) => {
                      handleFile(e);
                      e.target.value = '';
                    }}
                    disabled={isUploadingImage || isUploadingMobileImage}
                  />
                  <FileSelect htmlFor='mobile-image-banner-file-input'>
                    {isUploadingMobileImage ? <SmallLoader height={20} color='#0b99e6' /> : buttonText}
                  </FileSelect>
                </Block>
              </>
            )}

            {(requiresDesktopImage || requiresMobileImage) && (
              <>
                <Separator />

                <Block>
                  <Title>Image Alt Text</Title>
                  <Body>Describe your image to improve accessibility and SEO.</Body>
                  <InputContainer>
                    <Input
                      type='text'
                      value={alt}
                      onChange={handleSetAlt}
                      data-testid={TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT}
                    />
                    <ValidateCheckIcon
                      valid={validateAlt(alt) ? 1 : 0}
                      data-testid={TEST_ID_IMAGE_BANNER_MODAL_ALT_INPUT_VALID}
                    />
                  </InputContainer>
                </Block>
              </>
            )}
          </RightColumnContent>
        </RightColumn>
      </Content>
      <StyledButton
        onClick={handleSave}
        disabled={
          isUploadingImage ||
          isUploadingMobileImage ||
          (requiresDesktopImage && !image) ||
          (requiresMobileImage && !mobileImage) ||
          (!requiresMobileImage && !requiresDesktopImage) ||
          (!showBannerSegmentation && !(validateURL(defaultLink) || defaultLink === ''))
        }
      >
        SAVE
      </StyledButton>
    </ModalContainer>
  );
}
